import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { usePageMeta } from '@/hooks/usePageMeta';
import { getServicesPageMeta } from '@/seo/meta';
import { homeData } from '@/data/homeContent';
import { siteManifest } from '@/config/site';
import { FadeInUp } from '@/components/ui/FadeInUp';
import AccreditationBar from '@/components/sections/AccreditationBar';
import AccreditationShowcase from '@/components/sections/AccreditationShowcase';
import ContactCta from '@/components/sections/ContactCta';

type ServiceItem = { title: string; description: string };
type ProcessStep = { no: string; title: string; description: string };

export default function Services() {
  const { t } = useTranslation();
  usePageMeta(getServicesPageMeta());

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const services = t('services.items', { returnObjects: true }) as ServiceItem[];
  const steps = t('process.steps', { returnObjects: true }) as ProcessStep[];
  const supports = t('accreditation.supports', { returnObjects: true }) as string[];
  const accreditation = siteManifest.accreditation;

  return (
    <>
      <div
        className="page-header parallaxie"
        style={{
          backgroundImage: 'linear-gradient(rgba(25,35,36,0.6), rgba(25,35,36,0.6)), url(/images/page-header-bg.jpg)',
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
        }}
      >
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="page-header-box">
                <h1>{t('services.pageTitle')}</h1>
                <nav>
                  <ol className="breadcrumb">
                    <li className="breadcrumb-item"><a href="/">{t('common.home')}</a></li>
                    <li className="breadcrumb-item active">{t('services.breadcrumb')}</li>
                  </ol>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Capability cards */}
      <section className="mgc-section">
        <div className="container">
          <div className="mgc-section-head">
            <div>
              <h2 className="mgc-heading">{t('services.title')}</h2>
            </div>
            <p className="mgc-lead">{t('services.description')}</p>
          </div>
          <FadeInUp className="mgc-cards">
            {services.map((item, i) => (
              <div className="mgc-card" key={i}>
                <div className="mgc-card-icon">
                  <i className={`fa-solid ${homeData.services[i]?.icon || 'fa-building'}`} aria-hidden="true" />
                </div>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
              </div>
            ))}
          </FadeInUp>
        </div>
      </section>

      {/* Delivery process */}
      <section className="mgc-section mgc-section--light">
        <div className="container">
          <div className="mgc-section-head">
            <div>
              <span className="mgc-eyebrow">{t('process.subTitle')}</span>
              <h2 className="mgc-heading">{t('process.title')}</h2>
            </div>
          </div>
          <div className="delivery-grid">
            {steps.map((step, i) => (
              <div className="delivery-step" key={i}>
                <span className="delivery-step-no">{step.no}</span>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Accreditation bar (merged in) */}
      <AccreditationBar />

      {/* Confidence through accredited systems (merged in) */}
      <section className="mgc-section">
        <div className="container">
          {/* Full-width title */}
          <div className="row">
            <div className="col-lg-12">
              <span className="mgc-eyebrow">{t('accreditation.subTitle')}</span>
              <h2 className="mgc-heading" style={{ marginBottom: 44 }}>{t('accreditation.title')}</h2>
            </div>
          </div>
          {/* Left: text (aligned with the title) — Right: accreditation image */}
          <div className="row">
            <div className="col-lg-7">
              <p className="mgc-lead" style={{ marginBottom: 26, maxWidth: 'none' }}>{t('accreditation.body')}</p>
              <h3 style={{ fontSize: 20, fontWeight: 700, marginBottom: 8 }}>{t('accreditation.supportsLabel')}</h3>
              <FadeInUp>
                <ul className="accreditation-supports">
                  {supports.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </FadeInUp>
              <p className="mgc-lead" style={{ marginTop: 28, maxWidth: 'none' }}>{t('accreditation.closing')}</p>
            </div>
            <div className="col-lg-5">
              {accreditation && <AccreditationShowcase images={accreditation.images} maxWidth={300} />}
            </div>
          </div>
        </div>
      </section>

      <ContactCta />
    </>
  );
}
